/**
 * Funções utilitárias para componentes de comparação de períodos
 */

/**
 * Intercala dois arrays alternando elementos entre eles
 * 
 * @param {Array} array1 - Primeiro array
 * @param {Array} array2 - Segundo array
 * @returns {Array} Array intercalado
 */
export const interleaveArrays = (array1 = [], array2 = []) => {
    const result = [];
    const maxLength = Math.max(array1.length, array2.length);
    
    for (let i = 0; i < maxLength; i++) {
        if (i < array1.length) {
            result.push(array1[i]);
        }
        if (i < array2.length) {
            result.push(array2[i]);
        }
    }
    
    return result;
};

/**
 * Calcula percentuais com arredondamento inteligente que garante soma = 100%
 * 
 * @param {Array<number>} values - Array de valores
 * @param {number} total - Total para cálculo de percentuais
 * @returns {Map<number, number>} Mapa com índice -> percentual arredondado
 */
export const calculatePercentages = (values = [], total = 0) => {
    if (total === 0 || values.length === 0) {
        return new Map();
    }
    
    const percentages = new Map();
    const items = [];
    let sumRounded = 0;
    
    // Calcula porcentagens e arredonda
    values.forEach((value, index) => {
        const percent = (value / total) * 100;
        const roundedPercent = Math.round(percent * 100) / 100; // Arredonda para 2 casas decimais
        items.push({ index, percent: roundedPercent, originalPercent: percent });
        sumRounded += roundedPercent;
    });
    
    // Ajusta diferença para garantir soma = 100%
    const diff = 100 - sumRounded;
    if (Math.abs(diff) >= 0.005) { // Se diferença >= 0.005%
        // Ordena por maior diferença entre original e arredondado
        items.sort((a, b) => Math.abs(b.originalPercent - b.percent) - Math.abs(a.originalPercent - a.percent));
        // Ajusta o primeiro item (com maior diferença) para compensar
        const adjustValue = diff > 0 ? 0.01 : -0.01;
        items[0].percent = Math.round((items[0].percent + adjustValue) * 100) / 100;
    }
    
    // Cria mapa com índices
    items.forEach(({ index, percent }) => {
        percentages.set(index, Math.max(0, percent)); // Garante que não seja negativo
    });
    
    return percentages;
};

/**
 * Formata um range de datas para exibição
 * 
 * @param {Array} dateRange - Array com [dataInicio, dataFim]
 * @param {string} locale - Locale para formatação ('pt-BR' ou 'en-US')
 * @returns {string} String formatada do range de datas
 */
export const formatDateRange = (dateRange, locale = 'pt-BR') => {
    if (!dateRange || !Array.isArray(dateRange) || dateRange.length < 2) {
        return '';
    }
    
    const [start, end] = dateRange;
    if (!start || !end) {
        return '';
    }
    
    // Usa dayjs se disponível, senão usa Date nativo
    try {
        const dayjs = require('dayjs');
        const format = locale === 'en-US' ? 'MMM DD, YYYY' : 'DD/MM/YYYY';
        const startFormatted = dayjs(start).format(format);
        const endFormatted = dayjs(end).format(format);
        return `${startFormatted} - ${endFormatted}`;
    } catch (e) {
        // Fallback para Date nativo
        const startDate = new Date(start);
        const endDate = new Date(end);
        const options = { 
            year: 'numeric', 
            month: locale === 'en-US' ? 'short' : '2-digit', 
            day: '2-digit' 
        };
        return `${startDate.toLocaleDateString(locale, options)} - ${endDate.toLocaleDateString(locale, options)}`;
    }
};

/**
 * Valida se um array é válido e não vazio
 * 
 * @param {*} value - Valor a ser validado
 * @returns {boolean} True se for array válido e não vazio
 */
export const isValidArray = (value) => {
    return Array.isArray(value) && value.length > 0;
};
